import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarClock, Menu, LogOut, Home, Users, ClipboardCheck, ClipboardList, BarChart3, Newspaper, Bot, Trash2, Ban, Plus, Phone, Mail, XCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const AdminAgendamentos = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [ags, setAgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [bloqueios, setBloqueios] = useState([]);
  const [filtro, setFiltro] = useState('agendado');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [bDate, setBDate] = useState('');
  const [bTime, setBTime] = useState('');
  const [bMotivo, setBMotivo] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${API}/admin/verify`, { withCredentials: true });
        localStorage.removeItem('admin_token');
        fetchData();
      } catch (error) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    };
    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => { if (!loading) fetchAgs(); /* eslint-disable-next-line */ }, [filtro]);

  const fetchAgs = async () => {
    try {
      const res = await axios.get(`${API}/agendamento/admin?status=${filtro}`, { withCredentials: true });
      setAgs(res.data.agendamentos || []);
      setTotal(res.data.agendados_total || 0);
    } catch (error) { logger.error(error); }
  };

  const fetchData = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/agendamento/admin?status=${filtro}`, { withCredentials: true }),
        axios.get(`${API}/agendamento/bloqueios`, { withCredentials: true }),
      ]);
      setAgs(a.data.agendamentos || []);
      setTotal(a.data.agendados_total || 0);
      setBloqueios(b.data.bloqueios || []);
    } catch (error) {
      toast({ title: 'Erro ao carregar', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const cancelar = async (id) => {
    if (!window.confirm('Cancelar este atendimento? O horário será liberado.')) return;
    try {
      await axios.post(`${API}/agendamento/${id}/cancelar`, {}, { withCredentials: true });
      toast({ title: 'Atendimento cancelado' });
      fetchData();
    } catch (error) { toast({ title: 'Erro ao cancelar', variant: 'destructive' }); }
  };

  const criarBloqueio = async (e) => {
    e.preventDefault();
    if (!bDate) { toast({ title: 'Informe a data do bloqueio', variant: 'destructive' }); return; }
    try {
      await axios.post(`${API}/agendamento/bloqueios`, { date: bDate, time: bTime || null, motivo: bMotivo || null }, { withCredentials: true });
      toast({ title: 'Bloqueio criado', description: bTime ? `${bDate} às ${bTime}` : `Dia inteiro ${bDate}` });
      setBDate(''); setBTime(''); setBMotivo('');
      fetchData();
    } catch (error) {
      toast({ title: 'Erro', description: error.response?.data?.detail || 'Não foi possível criar o bloqueio.', variant: 'destructive' });
    }
  };

  const removerBloqueio = async (id) => {
    try {
      await axios.delete(`${API}/agendamento/bloqueios/${id}`, { withCredentials: true });
      toast({ title: 'Bloqueio removido' });
      fetchData();
    } catch (error) { toast({ title: 'Erro ao remover', variant: 'destructive' }); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const fmt = (iso) => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };

  const navItem = (path, Icon, label, active, testid) => (
    <button onClick={() => navigate(path)} data-testid={testid} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${active ? 'bg-yellow-500/20 text-yellow-400' : 'text-gray-400 hover:bg-gray-800'}`}>
      <Icon className="w-5 h-5" />{sidebarOpen && <span>{label}</span>}
    </button>
  );

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><div className="text-white text-xl">Carregando...</div></div>;
  }

  return (
    <div className="min-h-screen bg-black flex">
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-2"><Menu className="w-6 h-6" /></button>
          </div>
          <AdminNav active="agendamentos" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Agendamentos</h1>
            <p className="text-gray-400">{total} atendimento(s) ativo(s)</p>
          </div>

          {/* Bloqueios */}
          <div className="glass-dark p-6 rounded-xl border border-red-500/20 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Ban className="w-5 h-5 text-red-400" />
              <h2 className="text-lg font-bold text-white">Bloquear horários (feriados / folgas)</h2>
            </div>
            <form onSubmit={criarBloqueio} className="flex flex-col md:flex-row gap-3 mb-4">
              <input type="date" value={bDate} onChange={(e) => setBDate(e.target.value)} data-testid="bloqueio-date" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-yellow-500" />
              <input type="time" step="600" value={bTime} onChange={(e) => setBTime(e.target.value)} data-testid="bloqueio-time" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-yellow-500" placeholder="Opcional" />
              <input type="text" value={bMotivo} onChange={(e) => setBMotivo(e.target.value)} data-testid="bloqueio-motivo" placeholder="Motivo (opcional)" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500" />
              <button type="submit" data-testid="bloqueio-submit" className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-lg font-medium transition-colors">
                <Plus className="w-5 h-5" /> Bloquear
              </button>
            </form>
            <p className="text-xs text-gray-500 mb-3">Deixe o horário em branco para bloquear o dia inteiro.</p>
            {bloqueios.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhum bloqueio ativo.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {bloqueios.map((b) => (
                  <span key={b.id} data-testid={`bloqueio-item-${b.id}`} className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-3 py-1.5 text-sm">
                    {fmt(b.date)}{b.time ? ` às ${b.time}` : ' (dia inteiro)'}{b.motivo ? ` — ${b.motivo}` : ''}
                    <button onClick={() => removerBloqueio(b.id)} data-testid={`bloqueio-remover-${b.id}`} className="text-red-400 hover:text-red-200"><XCircle className="w-4 h-4" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Lista de agendamentos */}
          <div className="flex items-center gap-2 mb-4">
            {['agendado', 'cancelado', 'todos'].map((f) => (
              <button key={f} onClick={() => setFiltro(f)} data-testid={`filtro-${f}`} className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${filtro === f ? 'bg-yellow-500 text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                {f === 'agendado' ? 'Ativos' : f === 'cancelado' ? 'Cancelados' : 'Todos'}
              </button>
            ))}
          </div>

          <div className="glass-dark rounded-xl border border-white/10 overflow-hidden">
            {ags.length === 0 ? (
              <p className="text-gray-400 text-center py-12" data-testid="agendamentos-empty">Nenhum agendamento.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800 bg-white/5">
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Cliente</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Contato</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Data / Hora</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Status</th>
                      <th className="text-center py-4 px-4 text-gray-400 font-medium text-sm">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ags.map((a) => (
                      <tr key={a.id} className="border-b border-gray-800 hover:bg-white/5 transition-colors" data-testid={`agendamento-row-${a.id}`}>
                        <td className="py-4 px-4 text-white font-medium">{a.nome}</td>
                        <td className="py-4 px-4">
                          <div className="text-gray-300 text-sm flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{a.lead_email}</div>
                          {a.telefone && <div className="text-gray-500 text-xs flex items-center gap-1 mt-1"><Phone className="w-3.5 h-3.5" />{a.telefone}</div>}
                        </td>
                        <td className="py-4 px-4 text-white font-semibold">{fmt(a.date)} <span className="text-yellow-400">{a.time}</span></td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${a.status === 'agendado' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                            {a.status === 'agendado' ? 'Ativo' : 'Cancelado'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center">
                            {a.status === 'agendado' ? (
                              <button onClick={() => cancelar(a.id)} data-testid={`agendamento-cancelar-${a.id}`} className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Cancelar">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : <span className="text-gray-600 text-xs">—</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAgendamentos;
